import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosResponse } from 'axios';
import { axiosClient } from "../axiosClient";
import { IncomingMessage, NewMessage, NewTwilioConversation, TwilioConferenceParticipant, TwilioConversation } from "../../models/TwilioConversation";
import { BurnerCall, FinishTwilioTransferBody, JoinTwilioConferenceBody, StartTwilioConferenceBody, StartTwilioConferenceResponse, StartTwilioTransferenceBody, TwilioCall } from "../../models/TwilioCall";
import { Call, Device } from '@twilio/voice-sdk';
import { ConversationType } from "../../enums/ConversationType";
import { Media } from "../../models/TwilioConversation";
import { bool, number } from "prop-types";
import { nextPage } from "../commissions/action";

interface PhoneItem {
  id: string;
  value: string;
}

interface FetchCallsParams {
  pageSize?: number;
  outboundPageToken?: string;
  inboundPageToken?: string;
}

interface FetchConversationsParams {
  pageSize: number;
  pageToken: string;
}

interface CallHistoryResponse {
  calls: TwilioCall[];
  pageSize: number;
  nextOutboundPageToken?: string;
  nextInboundPageToken?: string;
}

interface PaginatedConversations {
  data: TwilioConversation[];
  pageSize: number;
  nextPageToken?: string;
}

export const fetchVoiceToken = createAsyncThunk("voiceToken", async () => {
    const response: AxiosResponse<string[]> = await (await axiosClient()).get(`/twilio/voice-token`);
    return response.data
});

export const fetchChatToken = createAsyncThunk("chatToken", async (identity:string) => {
    const response: AxiosResponse<string[]> = await (await axiosClient()).get(`/twilio/chat-token/${identity}`);
    return response.data
});

export const fetchConversations = createAsyncThunk("conversations", async () => {
    const response: AxiosResponse<PaginatedConversations> = await (await axiosClient()).get(`/twilio/get-conversations`);
    return response.data
});

export const fetchCalls = createAsyncThunk("calls", async (params: FetchCallsParams = {}) => {
    const { pageSize = 10, outboundPageToken, inboundPageToken } = params;
    const response: AxiosResponse<CallHistoryResponse> = await (await axiosClient()).get(`/twilio/get-calls`,
    { params: { pageSize, ...(outboundPageToken ? { outboundPageToken } : {}), ...(inboundPageToken ? { inboundPageToken } : {}),},});
    return response.data;
  }
);

export const fetchConversationMessages = createAsyncThunk("conversations/messages", async (params: {conversationSid:string}) => {
    const response: AxiosResponse<TwilioConversation[]> = await (await axiosClient()).get(`/twilio/get-messages?conversationSid=${params.conversationSid}`);
    return {
        messages: response.data,
        sid: params.conversationSid
    }
});

export const fetchConversationResource = createAsyncThunk("conversations/messages/resource", async (params: {sid: string}) => {
    const response: AxiosResponse<Media> = await (await axiosClient()).get(`/twilio/get-conversation-resource/${params.sid}`);
    return response.data;
});

export const createConversation = createAsyncThunk("conversations/new", async (params: {newConversation: NewTwilioConversation, conversationType: ConversationType}, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<TwilioConversation> = await (await axiosClient()).post(`/twilio/create-conversation`, params.newConversation);
        return {...response.data, conversationType: params.conversationType};
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const sendMessage = createAsyncThunk("conversations/sendMessage", async (message: NewMessage, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<string> = await (await axiosClient()).post(`/twilio/create-message`, message);
        return response.data
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const handleIncomingMessage = createAsyncThunk("conversations/incomingMessage", async (message: IncomingMessage) => {
    return message;
});

export const markMessageAsRead = createAsyncThunk("conversations/readMessage", async (conversationSid: string) => {
    return conversationSid;
});

export const setTwilioDeviceState = createAsyncThunk("twilioDevice/state", async (state: Device.State) => {
    return state;
});

export const removeCurrentConversation = createAsyncThunk("twilioDevice/currentConversation", async () => {
    return true;
});

export const setTwilioDevice = createAsyncThunk("twilioDevice", async (device?: Device) => {
    return device;
});

export const health = createAsyncThunk("twilioHealth", async () => {
    const response: AxiosResponse<boolean> = await (await axiosClient()).get(`/twilio/health`);
    return response.data
});

export const fetchPhoneContact = createAsyncThunk("conversations/phonecontacts", async (clientPhone: string): Promise<any> =>{
    const response: AxiosResponse<any> = await (await axiosClient()).get(`/twilio/get-phone-contact-call`, { params: { clientPhone } })
    return response.data
})

export const startTranferConference = createAsyncThunk("calls/startTranferConference", async (params: StartTwilioTransferenceBody, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<StartTwilioConferenceResponse> = await (await axiosClient()).post(`/twilio/start-transfer-conference`, params);
        return { ...response.data, transferTo: params.transferTo };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const finishTranferConference = createAsyncThunk("calls/finishTranferConference", async (params: FinishTwilioTransferBody, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<any> = await (await axiosClient()).post(`/twilio/finish-transfer-conferece`, params);
        return { ...response.data };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const startConferenceFromCall = createAsyncThunk("calls/startConferenceFromCall", async (params: StartTwilioConferenceBody, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<StartTwilioConferenceResponse> = await (await axiosClient()).post(`/twilio/start-conference-from-call`, params);
        return { ...response.data };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const joinConference = createAsyncThunk("calls/joinConference", async (params: JoinTwilioConferenceBody, { rejectWithValue }) => {
    try {
        const response: AxiosResponse<TwilioConferenceParticipant[]> = await (await axiosClient()).post(`/twilio/join-conference`, params);
        return { ...response.data };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const setPhoneBurner = createAsyncThunk("phoneBurner", async (list: BurnerCall[]) => {
    return list;
});

export const callNextPhoneInBurner = createAsyncThunk("phoneBurner/nextCall", async () => {
    return true;
});

export const removePhoneFromBurner = createAsyncThunk("phoneBurner/removeCall", async (index: number) => {
    return index;
});

export const cleanPhoneBurner = createAsyncThunk("phoneBurner/clean", async () => {
    return true;
});

export const setStateLeadCreated = createAsyncThunk("phoneBurner/isLeadCreated", async (value: boolean) => {
    return value;
});

export const startNewConference = createAsyncThunk("calls/startNewConference",async (params: string[], { rejectWithValue }) => {
    try {
        const response: AxiosResponse<any> = await (await axiosClient()).post(`/twilio/start-conference`, params);
        return { ...response.data };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
})

export const setConferenceId = createAsyncThunk("calls/setConferenceId", async (confereceId?: string) => {
    return confereceId;
});
